/**
 * Consent document versions API
 *
 *   GET    /api/v2/ConsentDocumentVersion        the published versions, newest first
 *   POST   /api/v2/ConsentDocumentVersion        publish a version
 *   DELETE /api/v2/ConsentDocumentVersion/{id}   withdraw one that has not taken effect
 *
 * Both require a system admin credential. Publishing evicts the API's cache, so a version marked
 * as requiring re-acceptance reaches users on their next page load.
 */

import constants from '../../../constants';
import { ConsentDocumentVersion } from '../../consentAudit/types';
import { PublishConsentDocumentVersion } from './types';

const ENDPOINT = `${constants.baseApiHost}/api/v2/ConsentDocumentVersion`;

// A corrupt or partially written session must not throw here. Falling back to no session sends an
// empty bearer token and surfaces an honest 401, which the page renders as an error state.
const getSession = () => {
  const raw = localStorage['session'];
  if (!raw || raw === 'undefined') {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: constants.apikey || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

// The endpoint answers with the API's { success, data } envelope, so a 200 carrying success: false
// is still a failure and both checks are needed. A gateway can answer with HTML or nothing at all,
// so an unreadable body is treated as the failure it is rather than surfacing a parser error.
const handleApiResponse = async <T>(
  response: Response,
  failureMessage: string
): Promise<T> => {
  // A successful DELETE answers 204 with no body, so there is nothing to parse.
  if (response.status === 204) {
    return undefined as T;
  }
  let json: { success?: boolean; data?: T } | null = null;
  try {
    json = await response.json();
  } catch {
    throw new Error(failureMessage);
  }
  if (!response.ok || !json?.success) {
    throw new Error(failureMessage);
  }
  return json.data as T;
};

export const getConsentDocumentVersions = async (): Promise<
  ConsentDocumentVersion[]
> => {
  const response = await fetch(ENDPOINT, { headers: getHeaders() });
  return handleApiResponse<ConsentDocumentVersion[]>(
    response,
    'consent document versions request failed'
  );
};

export const deleteConsentDocumentVersion = async (
  id: number
): Promise<void> => {
  const response = await fetch(`${ENDPOINT}/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  await handleApiResponse<unknown>(
    response,
    'withdrawing the consent document version failed'
  );
};

export const publishConsentDocumentVersion = async (
  model: PublishConsentDocumentVersion
): Promise<ConsentDocumentVersion> => {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(model)
  });
  return handleApiResponse<ConsentDocumentVersion>(
    response,
    'publishing the consent document version failed'
  );
};
